import { difference, isEmpty, sortBy, takeRight, uniq, uniqBy } from 'lodash'
import { useEffect, useMemo, useState } from 'react'

import { appendList } from 'lib/helpers'
import { Post, PostRender } from 'lib/_types'
import { useGetConversationRootPostsQuery } from 'store/api/yay/conversation'
import { useDeleteSelectedPostsMutation } from 'store/api/yay/post'

const MAX_LENGTH = 3

export const useOrderPostsByRepliesAndFetchRootPost = ({
  posts,
  pinnedPost = [],
}: {
  posts: Array<Post>
  pinnedPost?: Array<Post>
}) => {
  const [__conversationRootPosts, __conversationRootPostsSet] = useState<
    Array<Post>
  >([])

  const [_, deleteSelectedPostsEvent] = useDeleteSelectedPostsMutation({
    fixedCacheKey: 'deleteSelectedPosts',
  })

  // Get root posts for newly fetched conversations.
  // We shall not make a duplicated request for any existing root post.
  const _conversationIds = difference(
    uniq(posts?.map?.((x) => x?.conversation_id))?.filter?.((x) => !!x),
    __conversationRootPosts?.map?.((x) => x?.conversation_id)
  )

  const conversationsRootPostsRes = useGetConversationRootPostsQuery(
    {
      conversationIds: _conversationIds,
    },
    {
      skip: _conversationIds?.length == 0,
    }
  )

  useEffect(() => {
    if (
      deleteSelectedPostsEvent?.status == 'fulfilled' &&
      deleteSelectedPostsEvent?.data?.response?.result == 'success'
    ) {
      __conversationRootPostsSet((prevState) => {
        return prevState?.map?.((x) => {
          if (deleteSelectedPostsEvent?.data?.args?.includes?.(x?.id)) {
            return {
              ...x,
              // We shall not remove the deleted root post, but rather mark it as deleted.
              // Because if we remove it, `useGetConversationRootPostsQuery` will get triggered again
              // then also `conversationsRootPostsRes`'s `useEffect`, and the closure goes nuts.
              _deleted: true,
            }
          }

          return x
        })
      })
    }
  }, [deleteSelectedPostsEvent?.status])

  useEffect(() => {
    if (
      conversationsRootPostsRes?.isSuccess &&
      !conversationsRootPostsRes?.isFetching &&
      conversationsRootPostsRes?.currentData
    ) {
      __conversationRootPostsSet((prevPosts) =>
        appendList(prevPosts, conversationsRootPostsRes?.currentData?.posts)
      )
    }
  }, [conversationsRootPostsRes])

  const postsOrderedByReplies = useMemo(() => {
    const por: Array<PostRender> = []
    const pinnedList: Array<PostRender> = pinnedPost.map((x) => ({
      post: x,
      isConversationLayout: false,
      isConversationLayoutSmall: false,
      hasSeeMore: false,
    }))

    const pinnedIds = pinnedPost.map((x) => x.id)

    // sometimes root posts might not returned from root post API
    // but found in group timeline API result
    const _conversationRootPosts = uniqBy(
      [
        ...__conversationRootPosts,
        ...posts.filter((x) => x?.conversation_id && !x?.in_reply_to),
      ],
      (x) => x.id
    )

    posts?.forEach?.((currentPost) => {
      // For each post, we'll not consider post that:
      // - It is in `pinned`.
      // - It is already in `por`.
      // - It is belong to conversation that already in `por`.
      const isPostInPinned = pinnedIds.includes(currentPost.id)
      const isPostInPor = !!por?.find?.(
        (postInPor) => postInPor?.post?.id == currentPost?.id
      )
      const isConversationInPor =
        currentPost?.conversation_id &&
        !!por?.find?.(
          (postInPor) =>
            postInPor?.post?.conversation_id == currentPost?.conversation_id
        )
      if (isPostInPinned || isPostInPor || isConversationInPor) return

      // If a post is not a conversation, just append it normally.
      if (!currentPost?.conversation_id) {
        por.push({
          post: currentPost,
          isConversationLayout: false,
        })

        return
      }

      // If a post is a conversation post, we shall consider
      // all non-pinned posts in that conversation, sorting by old-to-new.
      let _conversationRootPost = _conversationRootPosts?.find?.(
        (x) => x?.conversation_id == currentPost?.conversation_id
      )

      // `_conversationRootPost` does not have `shareable` built-in.
      const originalSharable = posts?.find?.(
        (x) => x?.id == _conversationRootPost?.id
      )?.shareable

      if (!isEmpty(_conversationRootPost) && !isEmpty(originalSharable)) {
        _conversationRootPost = {
          ..._conversationRootPost,
          shareable: _conversationRootPost?.shareable ?? originalSharable,
        }
      }

      // `uniqBy` takes the leftmost entity. So in this case it will always be
      // a true root post from `__conversationRootPosts`.
      let conversationPosts = uniqBy(
        [
          ...(_conversationRootPost
            ? [_conversationRootPost]
            : conversationsRootPostsRes?.isFetching
            ? []
            : [
                {
                  _deleted: true,
                  // Need some unique number to be a key and id of `<PostItem />`.
                  id: Number(`${currentPost?.conversation_id}.1`),
                  // Need these for generate a proper `postLink`.
                  conversation_id: currentPost?.conversation_id,
                  group_id: currentPost?.group_id,
                  thread_id: currentPost?.thread_id,
                } as Post,
              ]),
          ...sortBy(
            posts?.filter?.(
              (postInInputPost) =>
                postInInputPost?.conversation_id == currentPost?.conversation_id
            ),
            (y) => y?.created_at
          ),
        ],
        (x) => x?.id
      ).filter((x) => !pinnedIds.includes(x.id))

      // First condition is trivial.
      //
      // Second condition is for when a non-root post of a conversation got replied,
      // thus the root post's `in_reply_to_post_count` does not get updated.
      // So a second guard can be derived by checking `conversationPosts?.length`.
      //
      // Third condition is for when the `conversationPosts` length is 3,
      // i.e. `_conversationRootPost?.in_reply_to_post_count = 2`.
      // But so far only 2 posts were present. In this case we should
      // encourage the user that there is one more post present
      // by showing `see more`. However, there is a chance that those replies
      // are from users you don't follow, so they will not appear in timeline results.
      // So you need to be able to see at least 2 posts of a conversation
      // to see the `see more`.
      const hasSeeMore =
        _conversationRootPost?.in_reply_to_post_count > MAX_LENGTH ||
        conversationPosts?.length > MAX_LENGTH ||
        (conversationPosts?.length > 1 &&
          conversationPosts?.length <=
            _conversationRootPost?.in_reply_to_post_count)

      // We then take the first, and the last two posts of the conversation.
      conversationPosts = uniqBy(
        [
          conversationPosts?.[0],
          ...takeRight(conversationPosts, MAX_LENGTH - 1),
        ],
        (x) => x?.id
      )

      // Then slap it right in place.
      por.push(
        ...conversationPosts?.map?.((x, i) => {
          return {
            post: x,
            isConversationLayout: i < conversationPosts?.length - 1,
            isConversationLayoutSmall: i != 0,
            hasSeeMore: i == 0 && hasSeeMore,
          }
        })
      )
    })

    return [...pinnedList, ...por]
  }, [posts, pinnedPost, conversationsRootPostsRes, __conversationRootPosts])

  return {
    __conversationRootPostsSet,
    postsOrderedByReplies,
  }
}

export const useOrderPostsByReplies = ({ posts }: { posts: Array<Post> }) => {
  const postsOrderedByReplies = useMemo(() => {
    const por: Array<PostRender> = []

    posts?.forEach?.((currentPost) => {
      // For each post, we'll not consider post that:
      // - It is already in `por`.
      // - It is belong to conversation that already in `por`.
      const isPostInPor = !!por?.find?.(
        (postInPor) => postInPor?.post?.id == currentPost?.id
      )

      const isConversationInPor =
        currentPost?.conversation_id &&
        !!por?.find?.(
          (postInPor) =>
            postInPor?.post?.conversation_id == currentPost?.conversation_id
        )

      if (isPostInPor || isConversationInPor) {
        return
      }

      // If a post is not a conversation, just append it normally.
      if (!currentPost?.conversation_id) {
        por.push({
          post: currentPost,
          isConversationLayout: false,
        })

        return
      }

      // If a post is a conversation post, we shall consider
      // all posts in that conversation, sorting by old-to-new.
      let conversationPosts = sortBy(
        posts?.filter?.(
          (postInInputPost) =>
            postInInputPost?.conversation_id == currentPost?.conversation_id
        ),
        (y) => y?.created_at
      )

      // For a pack of conversation to have a see more button,
      // it needs to have more posts in it more than the threshold.
      const hasSeeMore = conversationPosts?.length > MAX_LENGTH

      // We then take the first, and the last two posts of the conversation.
      conversationPosts = uniqBy(
        [
          conversationPosts?.[0],
          ...takeRight(conversationPosts, MAX_LENGTH - 1),
        ],
        (x) => x?.id
      )

      // Then slap it right in place.
      por.push(
        ...conversationPosts?.map?.((x, i) => {
          return {
            post: x,
            isConversationLayout: i < conversationPosts?.length - 1,
            isConversationLayoutSmall: i != 0,
            hasSeeMore: i == 0 && hasSeeMore,
          }
        })
      )
    })

    return por
  }, [posts])

  return postsOrderedByReplies
}
